import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../api/client';
import { fmtCHF2 } from '../utils/format';
import { ScenarioBudget, CATEGORIES_CONTRACTUELLES } from '../types';
import { ChoixTagScenario } from './TagScenarioRevenu';

interface Props {
  annee: number;
  // Scénario de lecture courant : une embauche projetée s'y rattache par défaut
  scenarioId: number | null;
  scenarios: ScenarioBudget[];
  onFermer: () => void;
  onCree: () => void;
}

export function NouvelEmployeModal({ annee, scenarioId, scenarios, onFermer, onCree }: Props) {
  const [nom, setNom] = useState('');
  const [categorie, setCategorie] = useState('Encadrement');
  const [debut, setDebut] = useState(`${annee}-01-01`);
  const [fin, setFin] = useState('');
  const [note, setNote] = useState('');
  const [moisEffet, setMoisEffet] = useState(`${annee}-01`);
  const [nominal, setNominal] = useState('');
  const [tauxPct, setTauxPct] = useState('100');
  const [tag, setTag] = useState<'definitif' | 'projete'>('definitif');
  const [scenarioCible, setScenarioCible] = useState<number | null>(scenarioId);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const nominalNum = Number(nominal);
  const tauxNum = Number(tauxPct) / 100;
  const valide = nom.trim().length > 0 && nominalNum >= 0 && tauxNum >= 0 && tauxNum <= 3 &&
    /^\d{4}-\d{2}$/.test(moisEffet) && commentaire.trim().length > 0 &&
    (tag !== 'projete' || scenarioCible != null);

  async function creer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.post('/salaires/employes', {
        nom: nom.trim(),
        categorie,
        debutActivite: debut || null,
        finActivite: fin || null,
        note: note || null,
        nominal: nominalNum,
        taux: tauxNum,
        moisEffet,
        tag,
        scenarioId: tag === 'projete' ? scenarioCible : null,
        commentaire: commentaire.trim(),
      });
      onCree();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Nouvel·le employé·e</div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body">
          {erreur && <div className="text-err mb-16">{erreur}</div>}
          <div className="flex flex-col gap-12">
            <div className="flex gap-12 flex-wrap">
              <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
                <label className="form-label">Nom & prénom</label>
                <input className="form-input w-full" placeholder="NOM Prénom" value={nom} onChange={(e) => setNom(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" title="Sert de repli tant qu'aucune répartition par fonction n'est définie">
                  Catégorie <span className="text-ink-3">(repli avant répartition)</span>
                </label>
                <select className="form-select" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
                  {CATEGORIES_CONTRACTUELLES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-12 flex-wrap">
              <div className="form-group">
                <label className="form-label">Début d'activité</label>
                <input type="date" className="form-input" value={debut} onChange={(e) => setDebut(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Fin d'activité <span className="text-ink-3">(optionnel)</span></label>
                <input type="date" className="form-input" value={fin} onChange={(e) => setFin(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-12 flex-wrap items-end">
              <div className="form-group">
                <label className="form-label">Salarié·e dès</label>
                <input type="month" className="form-input" value={moisEffet} onChange={(e) => setMoisEffet(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Salaire nominal mensuel (100 %)</label>
                <input type="number" className="form-input" style={{ width: 140 }} min={0} step={0.05}
                  placeholder="p. ex. 7500" value={nominal} onChange={(e) => setNominal(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Taux d'occupation (%)</label>
                <input type="number" className="form-input" style={{ width: 100 }} min={0} max={300} step={0.5}
                  value={tauxPct} onChange={(e) => setTauxPct(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Statut</label>
                <div className="selectable-group">
                  <ChoixTagScenario
                    tag={tag}
                    scenarioId={scenarioCible}
                    scenarios={scenarios}
                    onChange={(t, sc) => { setTag(t); setScenarioCible(sc); }}
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Note <span className="text-ink-3">(optionnel)</span></label>
              <input className="form-input w-full" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Commentaire (obligatoire)</label>
              <input className="form-input w-full" placeholder="p. ex. Engagement décidé le …"
                value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <span className="text-sm text-ink-2" style={{ marginRight: 'auto' }}>
            {nominalNum > 0 && tauxNum > 0 && <>Mensuel : <strong>CHF {fmtCHF2(nominalNum * tauxNum)}</strong> (13 mensualités)</>}
          </span>
          <button className="btn" onClick={onFermer}>Annuler</button>
          <button className="btn btn-accent" disabled={!valide || enCours} onClick={creer}>
            {enCours ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}
